/**
 * The decisions behind useServersVerdict: what the servers list and its failure mean, and when
 * a caller may decide.
 */

import { ApiError } from '@/api/apiError';
import { HTTP_STATUS } from '@/api/httpStatus';
import { GateStatus, type Server, type ServersVerdict } from '@/types/servers';

/** listServers succeeded — `Has` renders the app, `None` redirects to setup. */
export function getServersStatus(servers: Server[]): ServersVerdict {
    return servers.length > 0 ? GateStatus.Has : GateStatus.None;
}

/**
 * listServers failed — classify the failure. 403 means the active org is not accessible with
 * this session: a stale org id from an earlier login on this browser, a revoked membership, or a
 * session token whose baked-in org claim predates the membership. No amount of retrying fixes
 * that, so it gets its own status (`Forbidden`) and a sign-out recovery instead of the retry
 * screen. Everything else stays `Error` (transient backend blip).
 */
export function getServersFailureStatus(err: unknown): ServersVerdict {
    return err instanceof ApiError && err.status === HTTP_STATUS.FORBIDDEN
        ? GateStatus.Forbidden
        : GateStatus.Error;
}

/** Where the caller goes next, given whether a servers load is in flight and what it answered. */
export function getNextGateStatus(
    status: GateStatus, isFetching: boolean, verdict: ServersVerdict,
): GateStatus {
    switch (status) {
        case GateStatus.Awaiting:
            return isFetching ? GateStatus.Pending : GateStatus.Awaiting;
        case GateStatus.Pending:
            return isFetching ? GateStatus.Pending : verdict;
        default:
            return status;
    }
}
